import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe, Check, X, Plus } from "lucide-react";
import { CtaButton, MonoLabel, SecondaryButton } from "./kit";
import { FontPicker } from "./FontPicker";
import api, { formatApiErrorDetail } from "../lib/api";

export const BrandExtract = ({ onDone, compact = false }) => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [prop, setProp] = useState(null);
  const [moodInput, setMoodInput] = useState("");
  const [fonts, setFonts] = useState([]);

  useEffect(() => {
    api.get("/fonts").then(({ data }) => setFonts(data.fonts || [])).catch(() => {});
  }, []);

  const extract = async () => {
    setBusy(true); setErr(""); setProp(null);
    try {
      const { data } = await api.post("/brand-kit/extract", { url: url.trim() });
      if (data.status !== "ok") setErr(data.message);
      else {
        const f = data.proposal.fonts || {};
        setProp({
          ...data.proposal,
          mood_keywords: data.proposal.mood_keywords || [],
          fontPrimary: f.heading?.name || "",
          fontSecondary: f.body?.name && f.body.name !== f.heading?.name ? f.body.name : "",
          fontMeta: f,
        });
      }
    } catch {
      setErr("I couldn't read your site — upload your logo and pick colors manually.");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setSaving(true);
    try {
      if (prop.logo_b64) {
        const blob = await (await fetch(`data:image/png;base64,${prop.logo_b64}`)).blob();
        const fd = new FormData();
        fd.append("file", blob, "logo.png");
        await api.post("/brand-kit/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      const body = {};
      if (prop.primary) body.primary_color = prop.primary;
      if (prop.secondary) body.secondary_color = prop.secondary;
      if (prop.fontPrimary) {
        body.primary_font = prop.fontPrimary;
        body.secondary_font = prop.fontSecondary || "";
      }
      if (prop.mood_keywords.length) body.mood_keywords = prop.mood_keywords;
      if (Object.keys(body).length) await api.put("/brand-kit", body);
      toast("Brand Kit saved.", { description: "Every new creative will come out on-brand." });
      setProp(null); setUrl("");
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save — try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="brand-extract">
      <div className="flex items-end gap-2.5 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className="font-mono-j text-[9.5px] tracking-[0.12em] text-[#54505B] uppercase block mb-1.5">
            {compact ? "Have a website? Extract your brand" : "Have a website? Extract your brand automatically"}
          </label>
          <input className="field-input !py-2.5" placeholder="https://yourbusiness.com" value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && url.trim().length >= 4 && !busy) { e.preventDefault(); extract(); } }}
            data-testid="bx-url-input" />
        </div>
        <SecondaryButton className="!py-2.5 !px-4 text-[13px]" onClick={extract} disabled={busy || url.trim().length < 4} data-testid="bx-extract-btn">
          <Globe size={13} /> {busy ? "Reading…" : "Extract"}
        </SecondaryButton>
      </div>

      {err && <p className="text-[12.5px] text-[#96691E] mt-2.5" data-testid="bx-error">{err}</p>}

      {prop && (
        <div className="rounded-xl border border-[#EAE5DB] bg-[#FAF8F3] p-4 mt-3" data-testid="bx-proposal-card">
          <div className="flex items-center justify-between">
            <MonoLabel>HERE'S YOUR BRAND — CONFIRM OR ADJUST</MonoLabel>
            <button onClick={() => setProp(null)} className="text-[#9A9591] hover:text-[#181621]" data-testid="bx-close-btn"><X size={14} /></button>
          </div>
          <div className="flex flex-wrap items-start gap-5 mt-4">
            {prop.logo_b64 && (
              <div data-testid="bx-logo-preview">
                <p className="font-mono-j text-[9.5px] tracking-[0.12em] text-[#54505B] uppercase mb-1.5">Logo (transparent)</p>
                <div className="h-20 w-28 rounded-lg border border-[#EAE5DB] flex items-center justify-center p-2"
                  style={{ background: "repeating-conic-gradient(#eee 0% 25%, #fff 0% 50%) 0 0/16px 16px" }}>
                  <img src={`data:image/png;base64,${prop.logo_b64}`} alt="logo" className="max-h-full max-w-full object-contain" />
                </div>
              </div>
            )}
            <div>
              <p className="font-mono-j text-[9.5px] tracking-[0.12em] text-[#54505B] uppercase mb-1.5">Colors</p>
              <div className="flex gap-3">
                {["primary", "secondary"].map((k) => (
                  <div key={k} className="flex items-center gap-1.5">
                    <input type="color" value={prop[k] || "#cccccc"} onChange={(e) => setProp({ ...prop, [k]: e.target.value })}
                      className="w-9 h-9 rounded-lg border border-[#EAE5DB] cursor-pointer bg-white" data-testid={`bx-color-${k}`} />
                    <span className="font-mono-j text-[10.5px] text-[#54505B]">{prop[k] || "—"}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="font-mono-j text-[9.5px] tracking-[0.12em] text-[#54505B] uppercase mb-1.5">Fonts from your site</p>
              <div className="flex gap-4 flex-wrap">
                <div>
                  <FontPicker label="Primary" required value={prop.fontPrimary} onChange={(v) => setProp({ ...prop, fontPrimary: v })} fonts={fonts} placeholder="Pick…" testid="bx-font-primary" />
                  {prop.fontMeta?.heading && (
                    <p className="text-[10px] mt-1 font-semibold" style={{ color: prop.fontMeta.heading.exact ? "#3E7A4E" : "#96691E" }} data-testid="bx-font-primary-badge">
                      {prop.fontMeta.heading.exact ? "EXACT MATCH" : `CLOSEST TO "${prop.fontMeta.heading.original}" (not in list)`}
                    </p>
                  )}
                </div>
                <div>
                  <FontPicker label="Secondary" value={prop.fontSecondary} onChange={(v) => setProp({ ...prop, fontSecondary: v })} fonts={fonts} placeholder="Inter (default)" testid="bx-font-secondary" />
                  {prop.fontMeta?.body && prop.fontSecondary && (
                    <p className="text-[10px] mt-1 font-semibold" style={{ color: prop.fontMeta.body.exact ? "#3E7A4E" : "#96691E" }} data-testid="bx-font-secondary-badge">
                      {prop.fontMeta.body.exact ? "EXACT MATCH" : `CLOSEST TO "${prop.fontMeta.body.original}" (not in list)`}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <p className="font-mono-j text-[9.5px] tracking-[0.12em] text-[#54505B] uppercase mb-1.5">Mood keywords</p>
            <div className="flex items-center gap-2 flex-wrap">
              {prop.mood_keywords.map((m, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#EAE5DB] px-3 py-1.5 text-[12px] font-semibold" data-testid={`bx-mood-${i}`}>
                  {m}
                  <button onClick={() => setProp({ ...prop, mood_keywords: prop.mood_keywords.filter((_, j) => j !== i) })} className="text-[#9A9591] hover:text-[#181621]"><X size={11} /></button>
                </span>
              ))}
              {prop.mood_keywords.length < 8 && (
                <span className="inline-flex items-center gap-1">
                  <input className="field-input !py-1.5 !px-3 !text-[12px] w-[110px]" placeholder="add…" value={moodInput}
                    onChange={(e) => setMoodInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && moodInput.trim()) { setProp({ ...prop, mood_keywords: [...prop.mood_keywords, moodInput.trim()] }); setMoodInput(""); } }}
                    data-testid="bx-mood-input" />
                  <button disabled={!moodInput.trim()} onClick={() => { setProp({ ...prop, mood_keywords: [...prop.mood_keywords, moodInput.trim()] }); setMoodInput(""); }}
                    className="text-[#54505B] hover:text-[#181621] disabled:opacity-40" data-testid="bx-mood-add"><Plus size={14} /></button>
                </span>
              )}
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <CtaButton className="!py-2 !px-4 text-[13px]" onClick={confirm} disabled={saving} data-testid="bx-confirm-btn">
              <Check size={13} /> {saving ? "Saving…" : "Confirm & save"}
            </CtaButton>
          </div>
        </div>
      )}
    </div>
  );
};
